import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { useDeleteAuthApi } from '@/hooks/UseApiCalls';
import { ManagedUserDtoIn } from '@/models/in/ManagedUserDtoIn';
import { getUserFullName } from './userHelpers';

interface DeleteUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: ManagedUserDtoIn;
  onSuccess: () => void;
}

const DeleteUserDialog = ({ open, onClose, user, onSuccess }: DeleteUserDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { mutate: deleteUser, isPending: isDeleting } = useDeleteAuthApi<void>(`users/delete/${encodeURIComponent(user.email)}`);

  const handleDelete = () => {
    setError(null);
    deleteUser(undefined, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      // A refusal - a lead coordinator of a project - comes back in Hebrew, ready to show.
      onError: (err) => {
        const message = axios.isAxiosError(err) && typeof err.response?.data === 'string' ? err.response.data.trim() : '';
        setError(message || t('usersManagement.delete.error'));
      },
    });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  // The account is removed for good on the server, so the warning says so before the click.
  const confirmation = [
    t('usersManagement.delete.confirm', { name: getUserFullName(user) || user.email, email: user.email }),
    t('usersManagement.delete.note'),
    ...((user.fundIds?.length ?? 0) > 0 ? [t('usersManagement.delete.coordinatorNote')] : []),
  ].join('\n\n');

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={t('usersManagement.delete.title')}
      message={error ? `${confirmation}\n\n${error}` : confirmation}
      confirmText="usersManagement.delete.confirmButton"
      cancelText="cancel"
      onConfirm={handleDelete}
      onCancel={handleClose}
      isLoading={isDeleting}
      variant="danger"
    />
  );
};

export default DeleteUserDialog;
